import DownloadIcon from "@mui/icons-material/Download";
import ErrorIcon from "@mui/icons-material/Error";
import VerifiedIcon from "@mui/icons-material/Verified";
import WarningIcon from "@mui/icons-material/Warning";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import dayjs from "dayjs";
import { useLoaderData } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import DocumentSection from "./document-section";
import SignersSection from "./signers-section";
import { downloadEnvelopeZip } from "../../../api/validate";

function Result(): React.JSX.Element {
  const { t } = useTranslation();
  const document = useLoaderData() as ResponseSignaturesEnvelope;

  const handleDownloadZip = async (): Promise<void> => {
    try {
      const response = await downloadEnvelopeZip(document.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = window.document.createElement("a");
      link.href = url;
      link.setAttribute("download", `documentos-lote-${document.id}.zip`);
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(t("validate.downloadSuccess"));
    } catch (error) {
      toast.error(t("validate.downloadError"));
    }
  };

  const isExpiredOrCancelled =
    document.status === "EXPIRED" || document.status === "CANCELLED";
  const isPendingOrStarted =
    document.status === "PENDING" || document.status === "SIGNATURE_STARTED";
  const hasFiles = Boolean(document.files && document.files.length > 0);

  const getHeaderTitle = (): string => {
    if (document.status === "EXPIRED") {
      return t("validate.expired");
    }
    if (document.status === "CANCELLED") {
      return t("validate.cancelled");
    }
    if (isPendingOrStarted) {
      return t("validate.pending");
    }
    return t("validate.success");
  };

  const getHeaderIcon = (): React.JSX.Element => {
    if (isExpiredOrCancelled) {
      return <ErrorIcon color="error" sx={{ fontSize: 36 }} />;
    }
    if (isPendingOrStarted) {
      return <WarningIcon color="warning" sx={{ fontSize: 36 }} />;
    }
    return <VerifiedIcon color="success" sx={{ fontSize: 36 }} />;
  };

  const getStatusLabel = (status: string): string => {
    switch (status) {
      case "EXPIRED":
        return t("validate.fields.statusExpired");
      case "CANCELLED":
        return t("validate.fields.statusCancelled");
      case "PENDING":
        return t("validate.fields.statusPending");
      case "SIGNATURE_STARTED":
        return t("validate.fields.statusSignatureStarted");
      case "SIGNED":
      case "COMPLETED":
        return t("validate.fields.statusSigned");
      default:
        return status;
    }
  };

  return (
    <Stack spacing={3} sx={{ width: "100%" }}>
      <div>
        <Container>
          <Stack
            alignItems={{ xs: "center", md: "center" }}
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            spacing={2}
          >
            <Typography
              sx={{
                fontSize: { xs: "1.2rem", md: "1.5rem" },
                textAlign: { xs: "center", md: "left" },
              }}
              variant="h5"
            >
              {getHeaderTitle()}
            </Typography>
            <Stack
              alignItems="center"
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              sx={{ width: { xs: "100%", md: "auto" } }}
            >
              {!isExpiredOrCancelled && (
                <Button
                  disabled={!hasFiles}
                  onClick={() => void handleDownloadZip()}
                  startIcon={<DownloadIcon />}
                  sx={{ width: { xs: "100%", sm: "auto" } }}
                  variant="outlined"
                >
                  {t("validate.downloadZip")}
                </Button>
              )}
              {getHeaderIcon()}
            </Stack>
          </Stack>
        </Container>
      </div>
      <Box sx={{ backgroundColor: "#fafafa" }}>
        <Container sx={{ paddingY: 3 }}>
          <DocumentSection
            fields={[
              { label: t("validate.fields.author"), value: document.authorName },
              {
                label: t("validate.fields.status"),
                value: getStatusLabel(document.status),
              },
              {
                label: t("validate.fields.publishedAt"),
                value: dayjs(document.createdAt).format("DD/MM/YYYY HH:mm:ss"),
              },
              { label: t("validate.fields.hash"), value: document.hash },
            ]}
            files={document.files}
            hideViewIcon={isExpiredOrCancelled}
          />
        </Container>
      </Box>
      <Box>
        <Container>
          <SignersSection signers={document.signers} />
        </Container>
      </Box>
    </Stack>
  );
}

export default Result;
